import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  getAllergens,
  getDiets,
  getIngredients,
  getMenuTypes,
  getSpiceLevels,
  getUnits,
} from "@/api/Reference/ReferenceAPI";
import type {
  Allergen,
  Diet,
  Ingredient,
  MenuType,
  SpiceLevel,
  Unit,
} from "@/api/Reference/ReferenceAPI.model";
import { queryKeys } from "@/lib/queryKeys";

export interface DishReference {
  allergens: Allergen[];
  diets: Diet[];
  spiceLevels: SpiceLevel[];
  units: Unit[];
  ingredients: Ingredient[];
  menuTypes: MenuType[];
}

const EMPTY_ALLERGENS: Allergen[] = [];
const EMPTY_DIETS: Diet[] = [];
const EMPTY_SPICE: SpiceLevel[] = [];
const EMPTY_UNITS: Unit[] = [];
const EMPTY_INGREDIENTS: Ingredient[] = [];
const EMPTY_TYPES: MenuType[] = [];

/**
 * The lists needed to turn a dish's ids into readable details.
 *
 * Failure is silent by design: details are enrichment, and a dish must stay
 * orderable if, say, the allergen list is unavailable. Nothing here changes
 * during a meal, so the cache answers every screen after the first.
 */
export const useDishReference = (restaurantId: string): DishReference => {
  const enabled = Boolean(restaurantId);

  const [allergens, diets, spiceLevels, units, ingredients, menuTypes] =
    useQueries({
      queries: [
        { queryKey: queryKeys.allergens(), queryFn: getAllergens },
        { queryKey: queryKeys.diets(), queryFn: getDiets },
        { queryKey: queryKeys.spiceLevels(), queryFn: getSpiceLevels },
        { queryKey: queryKeys.units(), queryFn: getUnits },
        {
          queryKey: queryKeys.ingredients(restaurantId),
          queryFn: () => getIngredients(restaurantId),
          enabled,
        },
        {
          queryKey: queryKeys.menuTypes(restaurantId),
          queryFn: () => getMenuTypes(restaurantId),
          enabled,
        },
      ],
    });

  // One object, memoized: callers spread it (see OrderMenuTypePage) and a new
  // identity every render would re-run their own memos for nothing.
  return useMemo(
    () => ({
      allergens: allergens.data ?? EMPTY_ALLERGENS,
      diets: diets.data ?? EMPTY_DIETS,
      spiceLevels: spiceLevels.data ?? EMPTY_SPICE,
      units: units.data ?? EMPTY_UNITS,
      ingredients: ingredients.data ?? EMPTY_INGREDIENTS,
      menuTypes: menuTypes.data ?? EMPTY_TYPES,
    }),
    [
      allergens.data,
      diets.data,
      spiceLevels.data,
      units.data,
      ingredients.data,
      menuTypes.data,
    ],
  );
};
